import * as React from 'react';
import { type DayButton, DayPicker, type Locale, getDefaultClassNames } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  locale,
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3 font-inter group/calendar', className)}
      captionLayout={captionLayout}
      locale={locale}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString(locale?.code, { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn('flex gap-4 flex-col relative', defaultClassNames.months),
        month: cn('flex flex-col w-full gap-3', defaultClassNames.month),
        nav: cn(
          'flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          'inline-flex items-center justify-center size-7 rounded-full text-primary-60 hover:bg-primary-5 cursor-pointer select-none aria-disabled:opacity-50',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          'inline-flex items-center justify-center size-7 rounded-full text-primary-60 hover:bg-primary-5 cursor-pointer select-none aria-disabled:opacity-50',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'flex items-center justify-center h-7 w-full px-6',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'w-full flex items-center text-s font-semibold justify-center h-7 gap-1.5',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn('relative rounded-full', defaultClassNames.dropdown_root),
        dropdown: cn('absolute inset-0 opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'select-none font-inter font-semibold text-s text-primary-100',
          defaultClassNames.caption_label,
        ),
        table: 'w-full border-collapse',
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'text-primary-50 text-body-12 flex-1 select-none w-8',
          defaultClassNames.weekday,
        ),
        week: cn('flex w-full mt-1', defaultClassNames.week),
        week_number_header: cn('select-none w-8', defaultClassNames.week_number_header),
        week_number: cn('text-body-12 select-none text-primary-40', defaultClassNames.week_number),
        day: cn('relative w-8 h-8 p-0 text-center group/day select-none', defaultClassNames.day),
        today: cn('', defaultClassNames.today),
        outside: cn('text-primary-30', defaultClassNames.outside),
        disabled: cn('text-primary-30 opacity-50', defaultClassNames.disabled),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />;
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return <ChevronLeftIcon className={cn('size-4', className)} {...props} />;
          }

          if (orientation === 'right') {
            return <ChevronRightIcon className={cn('size-4', className)} {...props} />;
          }

          return <ChevronDownIcon className={cn('size-4', className)} {...props} />;
        },
        DayButton: ({ ...props }) => <CalendarDayButton locale={locale} {...props} />,
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  locale,
  ...props
}: React.ComponentProps<typeof DayButton> & { locale?: Partial<Locale> }) {
  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected={modifiers.selected}
      data-today={modifiers.today}
      className={cn(
        'inline-flex items-center justify-center size-7 rounded-full font-inter font-medium text-s text-primary-80 cursor-pointer outline-none transition-colors',
        'hover:bg-primary-5',
        modifiers.today && !modifiers.selected && 'bg-primary-10 text-primary-100',
        modifiers.selected && 'bg-primary-100 text-white hover:bg-primary-90',
        modifiers.outside && !modifiers.selected && 'text-primary-30',
        modifiers.disabled && 'opacity-50 cursor-default',
        className,
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
